import type { OnboardingPersona } from '@/components/onboarding/persona';

// Ad entry-point journeys. Each paid ad deep-links to one of four public
// entry pages; the page stashes its journey here so the rest of the funnel
// (signup copy, onboarding persona preset, post-onboarding landing) can
// shape itself without threading params through every redirect.
//
// Stored in BOTH sessionStorage and a short-lived first-party cookie.
// sessionStorage alone proved insufficient: it survives the email flow
// (never leaves the origin) but was lost across Clerk's Google OAuth
// redirect chain in real browsers, dropping SSO signups into the organic
// onboarding. The cookie is the proven carrier here — `signup_ref` rides
// the same hop for attribution. Reads prefer the per-tab sessionStorage
// value (two tabs on different journeys stay independent while it lives);
// the cookie is the OAuth-survivor fallback. Hygiene: cleared on every
// onboarding finish AND when an already-onboarded user passes
// /auth/finishing, plus a 1-hour TTL — so a shared browser's next signup
// can't inherit a stale journey for long.

export type EntryJourney =
  | 'individual'
  | 'start-circle'
  | 'join-circle'
  | 'join-network';

export const ENTRY_JOURNEY_STORAGE_KEY = 'entry.journey';

const ENTRY_JOURNEY_COOKIE = 'entry_journey';
// Matches the server's SIGNUP_ATTRIBUTION_WINDOW_MS spirit: a journey is a
// single signup session's intent, not a durable preference.
const COOKIE_MAX_AGE_SECONDS = 60 * 60;

const JOURNEY_VALUES = [
  'individual',
  'start-circle',
  'join-circle',
  'join-network',
] as const;

export function isEntryJourney(value: string | null): value is EntryJourney {
  return value !== null && (JOURNEY_VALUES as readonly string[]).includes(value);
}

function readCookie(): EntryJourney | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${ENTRY_JOURNEY_COOKIE}=([^;]+)`),
  );
  if (!m || m[1] === undefined) return null;
  const value = decodeURIComponent(m[1]);
  return isEntryJourney(value) ? value : null;
}

export function readEntryJourney(): EntryJourney | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(ENTRY_JOURNEY_STORAGE_KEY);
  if (isEntryJourney(raw)) return raw;
  return readCookie();
}

export function writeEntryJourney(journey: EntryJourney): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(ENTRY_JOURNEY_STORAGE_KEY, journey);
  document.cookie =
    `${ENTRY_JOURNEY_COOKIE}=${encodeURIComponent(journey)}; Path=/; SameSite=Lax; Max-Age=${COOKIE_MAX_AGE_SECONDS}`;
}

export function clearEntryJourney(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(ENTRY_JOURNEY_STORAGE_KEY);
  document.cookie = `${ENTRY_JOURNEY_COOKIE}=; Path=/; SameSite=Lax; Max-Age=0`;
}

// The persona each journey pre-answers. A journey CTA is an explicit role
// choice ("Start my Circle"), so the onboarding picker would be asking the
// same question twice. Invite-derived presets still win over this — an
// invite names a concrete group + role; an ad only names an intent.
export function personaForJourney(journey: EntryJourney): OnboardingPersona {
  switch (journey) {
    case 'start-circle':
      return 'circle-leader';
    case 'join-circle':
      return 'circle-member';
    case 'individual':
    case 'join-network':
      return 'lean-in';
  }
}
